import { ConversationProps } from '@/entities/conversation'
import { cn } from '@/lib/utils'
import { initChatStore, useChatStore } from '@/store/chat'
import { formatDate } from '@/utils/dates-util'
import { CircleDot, Dot, Share, Trash2 } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useToast } from '@/hooks/use-toast'
import { showConfirm } from '@/lib/alerts'
import { useAuthStore } from '@/store/auth'
import { UserProps } from '@/entities/user'
import { addHours } from 'date-fns'

interface ConversationCardProps {
  items: ConversationProps[]
}
export function ConversationCard({ items }: ConversationCardProps) {
  const { user } = useAuthStore()
  const location = useLocation()
  const { toast } = useToast()
  const { deleteConversation, shareConversation } = useChatStore()

  const handleShareConversation = (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    const url = shareConversation(id)

    if (!url) return
    navigator.clipboard.writeText(url)

    toast({
      variant: 'success',
      title: 'Sucesso',
      description: `Link da conversa copiado para a área de transferência`
    })
  }

  const handleDeleteConversation = async (ref: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!ref) return

    const { isConfirmed } = await showConfirm('Deseja deletar a conversa?')
    if (!isConfirmed) return

    const success = await deleteConversation(ref)
    if (success) {
      toast({
        variant: 'success',
        title: 'Sucesso',
        description: `Conversa deletada com sucesso`
      })

      initChatStore(user as UserProps)

      if (location.pathname.includes(ref)) {
        window.location.href = '/'
      }
    } else {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: `Não foi possível deletar a conversa`
      })
    }
  }

  return (
    <div className="flex flex-col items-center justify-center">
      {items?.map((conversation: ConversationProps) => {
        const isActive = location.pathname.includes(conversation.ref)
        return (
          <div
            key={conversation.id}
            className={cn(
              'my-1 w-[95%] rounded-lg border transition-all duration-200',
              isActive
                ? 'bg-primary/20 border-primary/40 shadow-soft'
                : 'border-border hover:bg-muted hover:border-border'
            )}
          >
            <button
              onClick={() => {
                window.location.href = `/chat/${conversation.ref}`
              }}
              className={cn(
                'w-full rounded-lg px-3 py-2 text-left transition-colors',
                isActive ? 'text-coentro' : 'text-foreground'
              )}
            >
              <div className="flex justify-between items-start">
                <h3 className={cn('font-medium truncate w-full text-sm', isActive && 'font-semibold')}>{conversation.title}</h3>
              </div>
              <div className="mt-1 flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{formatDate(addHours(new Date(conversation?.createdAt || new Date()), 3))}</span>
                <div className="flex space-x-1">
                  <button onClick={e => handleShareConversation(conversation?.ref || '', e)} className="p-1 rounded text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors">
                    <Share className="h-4 w-4" />
                  </button>
                  <button onClick={e => handleDeleteConversation(conversation?.ref || '', e)} className="p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </button>
          </div>
        )
      })}
    </div>
  )
}
